import React from 'react';
import styled from 'styled-components';

import useTranslation from 'hooks/use-translation';
import useCart from 'src/hooks/use-cart';
import usePaths from 'src/hooks/use-paths';
import useUI from 'src/hooks/use-ui';

import { Button } from 'components/core';
import { PaySignUpButton } from 'src/payments/components/pay-sign-up-button/pay-sign-up-button';
import { CartCashBackTreatment } from 'src/payments/components/cart-cash-back-treatment/cart-cash-back-treatment';
import { PointsBoostCartTreatment } from 'src/payments/components/points-boost-cart-treatment/points-boost-cart-treatment';
import { useDutchiePay } from 'src/payments/hooks';
import { useCartCashBackTreatment } from 'src/payments/hooks/use-cart-cash-back-treatment';
import { usePayByBankPointsBoost } from 'src/payments/hooks/use-pay-by-bank-points-boost';
import { usePointsBoostSurfacesConfig } from 'src/payments/hooks/use-points-boost-surfaces-config';
import { InterfaceVariants } from 'src/constants/interface-variants';
import { Footer, Subtotal, FooterSubtotal, Taxes } from '../cart-dropdown.styles';

type DutchiePayCheckoutProps = {
  isEmbedded: boolean;
  isMobile: boolean;
  handleGoToCheckout: () => void;
  subtotal: number;
  taxInclusionNote: string;
  isEnrolledDutchiePay: boolean;
};

export function DutchiePayCheckout({
  isEmbedded,
  isMobile,
  handleGoToCheckout,
  subtotal,
  taxInclusionNote,
  isEnrolledDutchiePay,
}: DutchiePayCheckoutProps): JSX.Element {
  const { t } = useTranslation();
  const { href: checkoutHref } = usePaths({ checkout: true });
  const { variant, isKiosk } = useUI();
  const { dispensary } = useCart();
  const { isDutchiePayEnabledForDispo } = useDutchiePay();
  const { showTreatment: showCashBackTreatment, cashBackValue } = useCartCashBackTreatment();

  // Same selection as the Cart V2 footer: points advertises independently of the cash gate,
  // sign-in, or enrollment; only structural kiosk/PBB gates apply.
  const pointsBoostCartEnabled = usePointsBoostSurfacesConfig().cart && !isKiosk && isDutchiePayEnabledForDispo;
  const { status: pointsBoostStatus, multiplier, multiplierLabel } = usePayByBankPointsBoost({
    dispensaryId: dispensary?.id,
    enabled: pointsBoostCartEnabled,
  });

  // At most one reward: cash-first fallback for accidental dual eligibility.
  const showCashBack = showCashBackTreatment && cashBackValue !== undefined;
  const showPointsBoost =
    !showCashBack &&
    pointsBoostCartEnabled &&
    pointsBoostStatus === 'available' &&
    multiplier !== null &&
    multiplierLabel !== null;
  const pointsBoostDisplayLabel = showPointsBoost ? multiplierLabel : null;
  const showTreatment = showCashBack || pointsBoostDisplayLabel !== null;

  const isKioskVariant = variant === InterfaceVariants.kiosk;

  // The treatments carry their own What is Pay by Bank link; suppress the button subtext
  // while a treatment is visible so the link never renders twice.
  const includeSubtext = !showTreatment;

  return (
    <Container isMobile={isMobile} $hasTreatment={showTreatment}>
      {!isMobile && (
        <TotalRow>
          <Subtotal data-testid='cart-dropdown-subtotal'>
            {t('common.subtotal', 'Subtotal')}: <strong>${subtotal.toFixed(2)}</strong>
          </Subtotal>
          <Taxes isEmbedded={isEmbedded}>{taxInclusionNote}</Taxes>
        </TotalRow>
      )}
      <ButtonContainer isMobile={isMobile}>
        {isMobile ? (
          <ProcessToCheckOutButtonMobile
            variant='primary'
            size='large'
            onClick={() => handleGoToCheckout()}
            data-testid='cart-checkout-button'
            isEmbedded={isEmbedded}
          >
            {t('common.proceedToCheckout', `Proceed to checkout - $${subtotal.toFixed(2)}`)}
          </ProcessToCheckOutButtonMobile>
        ) : (
          <ProcessToCheckOutButton
            variant='primary'
            size='large'
            onClick={() => handleGoToCheckout()}
            data-testid='cart-checkout-button'
            isEmbedded={isEmbedded}
          >
            {t('common.proceedToCheckout', 'Proceed to checkout')}
          </ProcessToCheckOutButton>
        )}
        {!isKioskVariant && (
          <DutchiePayButtonContainer>
            {isEnrolledDutchiePay ? (
              <PaySignUpButton
                goToCheckout={handleGoToCheckout}
                location='cart-rail'
                cashBackBadgeValue={showCashBack ? cashBackValue : undefined}
                pointsBoostLabel={pointsBoostDisplayLabel ?? undefined}
                includeSubtext={includeSubtext}
              />
            ) : (
              <PaySignUpButton
                refHref={checkoutHref}
                enrollmentSource={`cart-rail-${String(variant)}`}
                location='cart-rail'
                cashBackBadgeValue={showCashBack ? cashBackValue : undefined}
                pointsBoostLabel={pointsBoostDisplayLabel ?? undefined}
                includeSubtext={includeSubtext}
              />
            )}
          </DutchiePayButtonContainer>
        )}
        {showCashBack && (
          <TreatmentContainer>
            <CartCashBackTreatment />
          </TreatmentContainer>
        )}
        {pointsBoostDisplayLabel !== null && (
          <TreatmentContainer>
            <PointsBoostCartTreatment label={pointsBoostDisplayLabel} />
          </TreatmentContainer>
        )}
      </ButtonContainer>
    </Container>
  );
}

type ContainerStyleProps = { isMobile?: boolean; $hasTreatment?: boolean };

const containerHeight = ({ isMobile, $hasTreatment }: ContainerStyleProps): string => {
  if ($hasTreatment) {
    return `auto`;
  }
  return isMobile ? `154px` : `268px`;
};

const containerMinHeight = ({ isMobile, $hasTreatment }: ContainerStyleProps): string => {
  if (!$hasTreatment) {
    return `unset`;
  }
  return isMobile ? `154px` : `268px`;
};

const Container = styled(Footer)<ContainerStyleProps>`
  height: ${containerHeight};
  min-height: ${containerMinHeight};
  flex: 1;
  flex-direction: column;
  padding: 0;

  @media (min-width: 600px) {
    padding: 0;
  }
`;

const TreatmentContainer = styled.div`
  width: 100%;
  margin-top: 8px;
`;

const TotalRow = styled(FooterSubtotal)`
  width: 100%;
  padding: 14px;
  margin-bottom: 16px;
  background: #f3f6f8;
`;

const ButtonContainer = styled.div<{ isMobile?: boolean }>`
  width: calc(100% - 64px);
  display: flex;
  flex-direction: column;
  margin-bottom: ${({ isMobile }) => (isMobile ? `16px` : `34px`)};
`;

const DutchiePayButtonContainer = styled.div`
  margin-top: 20px;
`;

const ProcessToCheckOutButton = styled(Button)`
  border: 2px solid ${({ theme }) => theme.customized.colors.buttonsLinks};
  background: inherit;
  color: ${({ theme }) => theme.customized.colors.buttonsLinks};
  line-height: 15px;
`;

const ProcessToCheckOutButtonMobile = styled(ProcessToCheckOutButton)`
  margin-top: 16px;
  height: 43px;
  padding: 10px 22px;
  font-size: 13px;
`;
